<template>
  <div v-if="products?.length > 0" class="product-thumbnail-wrapper">
    <template v-for="(product, k) in products || []">
      <div
        v-if="!checkSameSellerItem(product.mkpLineItemDetails?.sellerId)"
        :key="k"
        class="grid-x"
        :class="[
          { 'border-bottom': !hideItemBorder },
          page === 'item-selection'
            ? 'padding-top-z padding-bottom-z'
            : page === 'purchase-details' && k === 0
              ? 'padding-top-s padding-bottom-m'
              : 'padding-top-m padding-bottom-m',
        ]"
      >
        <div :class="productThumbnailClass(product.returnEligibleFlag)">
          <div v-if="showCheckbox" class="align-self-middle margin-right-s">
            <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
            <input
              v-model="selectedProducts"
              type="checkbox"
              :disabled="showError(product)"
              :value="{ upc: product.upc, lineNumber: product.lineNumber, key: product.key, sellerId: product.mkpLineItemDetails?.sellerId }"
              @change="(validateSeller(), $emit('selectedProduct', selectedProducts))"
            />
          </div>
          <ProductThumbnail
            class="cell auto"
            alignment="horizontal"
            :image-width="imageWidth"
            :product="removeReturnWindow(product)"
            :url-template="product.imagery?.urlTemplate ? { product: product.imagery?.urlTemplate } : {}"
            :click-action="product.identifier?.productUrl ? 'fireEvent' : 'disabled'"
            :priority="k === 0 ? fetchpriority : 'auto'"
            :fetchpriority="k === 0 ? fetchpriority : 'auto'"
            :loading="k === 0 ? loading : 'lazy'"
            @get-product-details="getProductDetails"
          />
          <div class="cell" :class="{ 'margin-left-m': isAddReturn }">
            <ReturnPolicyMessage
              v-if="showPolicyMessage && !product.isLoyaltyByPassApplied"
              class="margin-top-xs"
              :class="{ 'medium-margin-bottom-xs': isVasBannerEligible(product) }"
              :product="product"
              :show-protection-plan-message="showProtectionPlanMessage(product)"
            />
            <div v-if="showPolicyMessage && product.isLoyaltyByPassApplied" class="padding-xxs margin-top-xs loyalty-bypass-container">
              <p>
                {{ config.PRODUCT_THUMBNAIL_WRAPPER.loyaltyByPassText }}
                <button class="tooltip-small loyalty-bypass-button" aria-label="More Information" @click="toggleTooltip($event)"></button>
              </p>
            </div>
            <div v-if="!isAddReturn && page !== 'item-selection'">
              <p v-if="isRefunded(product)" class="small margin-top-s">Refunded</p>
              <p v-if="isNonVirtualReturnReceived(product)" class="small margin-top-s">
                We have your return, please allow additional time for processing.
              </p>
              <p v-if="isVirtualExchange(product)" class="small margin-top-s">Exchanged for a new item</p>
              <p v-if="product.returnEligibleFlag && (product.isLtlItem || product.onlineOnlyReturn)" class="small margin-top-s">
                {{ config.PRODUCT_THUMBNAIL_WRAPPER.notEligibleInStoreReturns }}
              </p>
            </div>
            <div v-if="page === 'item-selection' || isAddReturn" :class="{ 'margin-top-xxs': isAddReturn }">
              <p v-if="shouldShowMessage(product, 'VR')">Refunded. Return by {{ formatDate(product.virtualReturnExpectedBackDate) }}</p>
              <p v-if="shouldShowMessage(product, 'VE')">Exchanged. Return by {{ formatDate(product.virtualReturnExpectedBackDate) }}</p>
            </div>
            <div v-if="['EGC', 'VGC'].includes(shipmentType) && giftMessage" class="margin-top-xs">
              <div class="gift-message padding-bottom-xss" :class="{ truncated: displayMoreLessToggle && !expandedText }">
                <div>Gift message:</div>
                {{ giftMessage }}
              </div>
              <button v-if="displayMoreLessToggle" id="view-more-toggle" @click="expandedText = !expandedText">
                <span class="link-sm" v-text="expandedText ? 'See less' : 'See more'"></span>
              </button>
            </div>
            <div v-if="showError(product)" class="error-msg margin-left-s">These items cannot be returned together.</div>
          </div>
        </div>
        <div
          v-if="hasCTA(product.returnEligibleFlag)"
          :class="[isSSREExchangeButtonEnabled ? 'medium-4' : 'medium-3', 'small-12 small-margin-top-xs medium-margin-top-z product-actions']"
        >
          <button
            v-if="product.returnEligibleFlag && ['order-details'].includes(page)"
            class="return-item-action secondary button button-small expanded margin-bottom-xs"
            @click="returnItemAction(product)"
          >
            {{
              isSSREExchangeButtonEnabled
                ? product.exchangeSubType || product?.shipmentType === 'RETURN'
                  ? 'Return'
                  : 'Return or Exchange'
                : 'Return'
            }}
          </button>
          <button
            v-if="showBuyAgain && product.detail.productUrl && !isRedCarpetShipment && product.buyAgainFlag"
            class="secondary button button-small expanded"
            @click="buyAgain(product)"
          >
            Buy Again
          </button>
          <div class="cta-actions">
            <div v-for="(button, index) in ctaButtonsConfig(product)" :key="index" class="text-center cta-box margin-top-xs margin-bottom-xs">
              <component :is="button.tag" :id="button.id" class="link-body" v-bind="button.attrs">
                {{ button.text }}
              </component>
            </div>
          </div>
          <div v-if="isContactSellerEnabled(product)" class="text-center margin-top-xs">
            <button id="contact-seller" class="link-body" @click="openContactSellerSlideout(product)">Contact seller</button>
          </div>
        </div>
        <div v-if="isVasBannerEligible(product)" class="small-12 product-actions">
          <VASBanner
            :order-number="product.orderNumber"
            :is-compact="true"
            :products="wnmItemDetails(product)"
            :online-purchase-eligibility="wnmOnlinePurchaseEligibility()"
          />
        </div>
      </div>
    </template>
    <Toggletip v-if="addTooltip" ref="tooltipRef" position="bottom" role="tooltip">
      <p>{{ config.PRODUCT_THUMBNAIL_WRAPPER.tooltipMessage }}</p>
    </Toggletip>
  </div>
</template>
<script setup>
import { ref, onMounted, onUnmounted, defineAsyncComponent, markRaw, computed } from 'vue';
import { fireTag } from '@vue-util/tagManager';
import { Experimentation } from '@vue-util/experimentation';
import { useProductQVStore } from '@nuxt-common/product-quickview/app/stores/useProductQVStore';
import Toggletip from '@atomic-ui/toggletip/Toggletip';
import ReturnPolicyMessage from '@/components/returns/ReturnPolicyMessage';
import { formatDate } from '@/utils/General';
import { useSlideout } from '@/composables/useSlideout';
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
import ProductThumbnail from '@/features/ProductThumbnail/components/ProductThumbnail';
import * as config from 'config';

const VASBanner = defineAsyncComponent(() => import('@nuxt-common/value-added-services/app/components/VASBanner/VASBanner'));

defineEmits(['selectedProduct']);
const props = defineProps({
  products: { type: Array, default: () => [] },
  returnStatus: { type: [String, Number], default: '' },
  showCheckbox: { type: Boolean, default: false },
  showBuyAgain: { type: Boolean, default: false },
  showPolicyMessage: { type: Boolean, default: false },
  isAddReturn: { type: Boolean, default: false },
  manageItemAction: { type: Function, default: () => {} },
  returnItemAction: { type: Function, default: () => {} },
  hideItemBorder: { type: Boolean, default: false },
  shipmentType: { type: String, default: '' },
  giftMessage: { type: String, default: '' },
  isMarketplaceOrder: { type: Boolean, default: false },
  currentSeller: { type: String, default: '' },
  page: { type: String, default: '' },
  isOldPpeFlow: { type: Boolean, default: false },
  manageItemIsAnchorTag: { type: Boolean, default: false },
  isRedCarpetShipment: { type: Boolean, default: false },
  isBuyAgainSlideoutEnabled: { type: Boolean, default: false },
  imageWidth: { type: Number, default: 130 },
  aspectRatio: { type: Number, default: 1 },
  fetchpriority: { type: String, default: 'auto' },
  loading: { type: String, default: 'lazy' },
});
const productQuickviewStore = useProductQVStore();
const orderDetailsStore = useOrderDetailsStore();
const { registerSlideout, toggleSlideout, closeSlideout } = useSlideout();

const selectedProducts = ref([]);
const displayMoreLessToggle = ref(true);
const expandedText = ref(false);
const selectedSellerId = ref(undefined);
const tooltipRef = ref(null);
const isWnmUpsellEnabled = ref(false);

const addTooltip = computed(() => props.products.some((product) => product.isLoyaltyByPassApplied));
const isSSREExchangeButtonEnabled = computed(() => orderDetailsStore.killSwitch?.ssreExchangeButtonEnabled);

const wnmUpsellState = computed(() => ({
  isFeatureEnabled: orderDetailsStore.killSwitch?.wnmUpsellFeatureEnabled,
  isWithoutExpEnabled: orderDetailsStore.killSwitch?.wnmUpsellWithoutExpEnabled,
  paymentEligible: orderDetailsStore.order?.wnmUpsellPaymentEligible,
  primaryShipmentType: orderDetailsStore.order?.primaryShipmentType,
  planPurchaseEndDate: orderDetailsStore.order?.wnmUpsellWindowDate,
}));

const isVasBannerEligible = (product) => {
  return props.page == 'order-details' && isWnmUpsellEnabled.value && product.wnmPurchaseEligibility;
};

// Data passed to VASBanner component for online purchase eligibility
const wnmOnlinePurchaseEligibility = () => {
  return {
    canPurchaseOnline: wnmUpsellState.value?.paymentEligible,
    planPurchaseEndDate: formatDate(wnmUpsellState.value.planPurchaseEndDate),
  };
};

const wnmItemDetails = (product) => {
  return [
    {
      itemId: product.id,
      isBT: wnmUpsellState.value?.primaryShipmentType === 'BT',
      quantity: product.detail?.quantity,
      upcNumber: product.detail?.upcNumber,
      unitPrice: parseFloat(product.unitPriceWithoutCurrency),
      lineItemId: Number(product.lineNumber),
    },
  ];
};

const showError = (product) => {
  const sellerId = props.currentSeller || selectedSellerId.value;
  return sellerId && product.mkpLineItemDetails?.sellerId !== sellerId;
};
const validateSeller = () => {
  if (!props.isOldPpeFlow) return;
  if (!selectedProducts.value.length) {
    selectedSellerId.value = undefined;
    return;
  }
  if (!selectedSellerId.value) selectedSellerId.value = selectedProducts.value[0].sellerId;
};
const fireTagForContactSeller = (product) => {
  fireTag('link', {
    event_name: 'content link click',
    link_name: 'marketplace:contact seller',
    site_action: 'marketplace contact seller start',
    action_location: 'order details',
    product_id: [product.id],
    product_upc: [product.upcNumber],
  });
};
const buyAgain = (product) => {
  fireTag('link', { event_name: 'buy again', link_name: 'buy again', product_finding_method: 'buy again' });
  if (props.isBuyAgainSlideoutEnabled) {
    productQuickviewStore.toggle({
      productId: product.id,
      slideoutTitle: 'Buy Again',
      swatchColor: product.detail?.colorAndSize[1],
      navigationContext: {},
    });
  } else {
    window.location.href = product.detail.productUrl;
  }
};
const openContactSellerSlideout = (product) => {
  const data = {
    sellerName: product.mkpLineItemDetails?.sellerName,
    partnerFulfillmentId: product.mkpLineItemDetails?.partnerFulfillmentId,
    lineNumber: product.lineNumber,
    mkpReturnReasons: product.mkpReturnReasons,
    orderNumber: product.orderNumber,
    productId: product.id,
    upcNumber: product.upcNumber,
  };
  registerSlideout({
    id: 'item-details-slideout',
    title: 'Contact Seller',
    props: { data },
    component: markRaw(defineAsyncComponent(() => import(/* webpackChunkName: "contact-seller" */ '@/components/marketplace/ContactSeller'))),
  });
  toggleSlideout('item-details-slideout');
  fireTagForContactSeller(product);
};
const isContactSellerEnabled = (product) => product.marketPlaceOrder && !product.mkpLineItemDetails.disableContactSeller;
const ctaButtonsConfig = (product) => {
  const buttons = [];

  if (product.beautyBoxProductFlag) {
    buttons.push({
      tag: 'a',
      text: 'Subscribe',
      id: 'manage-beauty-box-cta',
      attrs: {
        href: '/subscription/manageSubscriptions?lid=manage_subscription',
        target: '_blank',
      },
    });
  }

  if (!product.itemReviewed) {
    buttons.push({
      tag: 'a',
      text: 'Write a review',
      id: 'write-review-link',
      attrs: {
        href: `/shop/product/review/add?ID=${product.id}&CampaignID=PHOnline`,
        target: '_blank',
      },
    });
  } else {
    buttons.push({
      id: 'reviewed-text',
      tag: 'span',
      text: 'Reviewed',
    });
  }

  return buttons;
};
const isRefunded = (prod) => !!(prod?.virtualReturnItem && prod?.virtualReturnType === 'VR');

const isNonVirtualReturnReceived = (product = {}) => {
  const isReturnReceived = props.returnStatus && (props.returnStatus === 'DELIVERED' || props.returnStatus === 302);
  return isReturnReceived && !!(!product.virtualReturnItem && product.virtualReturnType !== 'VR');
};
const isVirtualExchange = (product = {}) => product.virtualReturnType?.toUpperCase() === 'VE';

const checkSameSellerItem = (sellerId) => {
  if (props.isMarketplaceOrder && !props.isOldPpeFlow) {
    return sellerId !== props.currentSeller;
  }
  return false;
};
const hasCTA = (itemReturnEligible) => props.page !== 'item-selection' && (props.showBuyAgain || itemReturnEligible);

const productThumbnailClass = (itemReturnEligible) => {
  if (props.showCheckbox) return 'cell grid-x ';
  if (!props.showCheckbox && hasCTA(itemReturnEligible)) {
    if (isSSREExchangeButtonEnabled.value) {
      return 'medium-8 small-12';
    }
    return 'medium-9 small-12';
  }
  return 'small-12';
};
const removeReturnWindow = (product) => {
  const modifiedProductData = { ...product };
  delete modifiedProductData.returnWindowDate;
  if (modifiedProductData.withProtectionPlan) {
    if (
      ['track-package'].includes(props.page) ||
      (['order-details', 'item-selection', 'add-more-item-selection'].includes(props.page) &&
        product.protectionPlanStatus?.toUpperCase() === 'ACTIVE')
    ) {
      delete modifiedProductData.protectionPlanStatus;
    }
  }
  return modifiedProductData;
};

const showProtectionPlanMessage = (product) =>
  !!(
    ['order-details', 'item-selection', 'add-more-item-selection'].includes(props.page) &&
    product.withProtectionPlan &&
    ['ACTIVE', 'PENDING'].includes(product.protectionPlanStatus?.toUpperCase())
  );

const isItemExpectedBackDateExpired = (expectedBackDate) => {
  const today = new Date();
  const date = new Date(expectedBackDate);
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date >= today;
};
const shouldShowMessage = (product, returnType) =>
  product.virtualReturnType?.toUpperCase() === returnType &&
  product.mosFlag.toUpperCase() === 'N' &&
  isItemExpectedBackDateExpired(product.virtualReturnExpectedBackDate);

onMounted(async () => {
  if (props.giftMessage) {
    const giftMessage = document.getElementsByClassName('gift-message');
    if (giftMessage[0]?.offsetHeight >= giftMessage[0]?.scrollHeight) {
      displayMoreLessToggle.value = false;
    }
  }
  isWnmUpsellEnabled.value =
    wnmUpsellState.value.isFeatureEnabled &&
    (wnmUpsellState.value.isWithoutExpEnabled || (await Experimentation.isRecipeSelected(config.WNM_UPSELL_EXPERIMENTATION_ID)));
});

onUnmounted(() => {
  closeSlideout('order-details-slideout');
});

const toggleTooltip = (event) => {
  tooltipRef.value.toggle(event);
};

const getProductDetails = (product = {}) => {
  if (['order-details', 'track-package'].includes(props.page)) {
    fireTag('link', { event_name: 'product click', link_name: 'product click' });
  }
  window.location.href = product.identifier?.productUrl;
};
</script>

<style lang="scss" scoped>
.track-package {
  .product-thumbnail-wrapper {
    .grid-x {
      &:last-child {
        border-bottom: none !important;
        padding-bottom: 0;
      }
    }
  }
}
@import '~@csgIcons/_error-red.scss';
@import '~@csgIcons/_tooltip-small.scss';
.gift-message {
  overflow: hidden;
  color: $gray-1-color;
  &.truncated {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
}

// TODO: remove after PROS-WGL CSG overrides issue resolved
.product-actions {
  // temporary fix for issue with CSG padding/margins with breakpoints
  margin-top: rem-calc(16) !important;
  // temporary fix for button text alignment issue
  a.button.expanded {
    display: inline-flex;
  }
}

// TODO: remove after PROS-WGL CSG overrides issue resolved
@media screen and (min-width: rem-calc(600)) {
  .product-actions {
    // temporary fix for issue with CSG padding/margins with breakpoints
    margin-top: 0 !important;
  }

  .cta-box {
    margin-bottom: 0;
  }
}

@media screen and (max-width: rem-calc(600)) {
  .product-actions {
    display: grid;
    grid-template-rows: auto auto;
  }

  .cta-actions {
    text-align: left;
    grid-row: 1;
    display: flex;
    flex-direction: row;
    gap: 1rem;

    .cta-box {
      margin-top: 0;
    }
  }
}

input[type='checkbox']:focus {
  border: 1px solid $black;
}

.error-msg {
  color: $error-color;
}

.loyalty-bypass-button {
  vertical-align: middle;
}
</style>
